type MapDiff<T> = Record<string, T | null>

/**
 * Compute a shallow diff between two flat objects.
 * The caller can apply it with:
 *   const patched = { ...from, ...diff }
 *   Object.keys(diff).forEach(k => diff[k] === null && delete patched[k])
 *
 * @param from   current state
 * @param to     desired state
 * @param equals optional equality fn (defaults to deepEqual)
 */
export function jsonMapDiff<T>(
  from: Record<string, T>,
  to: Record<string, T>,
  equals: (a: T, b: T) => boolean
): MapDiff<T> {
  const diff: MapDiff<T> = {}

  const fromKeys = new Set(Object.keys(from))
  const toKeys = new Set(Object.keys(to))

  // additions & updates
  for (const key of toKeys) {
    if (!equals(to[key], from[key])) {
      diff[key] = to[key]
    }
  }

  // removals
  for (const key of fromKeys) {
    if (!toKeys.has(key)) diff[key] = null
  }

  return diff
}

export function jsonMapPatch<T>(from: Record<string, T>, diff: MapDiff<T>): Record<string, T> {
  const patched = { ...from }
  Object.keys(diff).forEach((key) => {
    const value = diff[key]
    if (value === null) {
      // Deletion
      delete patched[key]
    } else {
      // Addition or Update
      patched[key] = value
    }
  })
  return patched
}
